import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RotateCcw, Snowflake, Waves, Wind, SlidersHorizontal, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { BreathingOrb } from "@/components/BreathingOrb";
import { SoundPicker } from "@/components/SoundPicker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { playCue, playIntro, setAudioSelection, startCalmTone, stopEmergencyAudio, DEFAULT_AUDIO } from "@/lib/audio";
import { useCards, usePrefs, useUpdateSession } from "@/lib/store";
import { defaultCrisisLine, telUrl } from "@/lib/support";

const BG = "https://images.unsplash.com/photo-1635776062360-af423602aff3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const TIP_HINTS = [
  { icon: Snowflake, text: "Splash cold water on your face" },
  { icon: Waves, text: "Hold ice against your wrists or neck" },
  { icon: Wind, text: "Long, slow exhale — twice as long as your inhale" },
];
const SETTLE_MS = 45000;

function OverrideCards({ cards }) {
  const [i, setI] = useState(0);
  const card = cards[i % cards.length];
  return (
    <div className="w-full max-w-md rounded-3xl bg-white/12 backdrop-blur-xl border border-white/20 p-5 mb-6 text-white tf-rise" data-testid="override-cards">
      <p className="text-xs uppercase tracking-widest text-white/70 font-bold">Your Healthy Adult voice</p>
      <p className="text-lg font-semibold mt-2">“{card.text}”</p>
      {cards.length > 1 ? (
        <button onClick={() => setI(i + 1)} className="mt-3 inline-flex items-center gap-1 text-sm text-white/80" data-testid="override-next-card">
          Next card ({(i % cards.length) + 1}/{cards.length}) <ArrowRight size={14} />
        </button>
      ) : null}
    </div>
  );
}

const CrisisStrip = ({ line }) => (
  <p className="mt-4 text-center text-[13px] text-white/75" data-testid="emergency-crisis-strip">
    Need to talk to someone now?{" "}
    <a href={telUrl(line.number)} className="font-semibold text-white underline underline-offset-2" data-testid="emergency-call-crisis">{line.name} {line.display || line.number}</a>
    {" · "}
    <a href={telUrl("1300224636")} className="font-semibold text-white underline underline-offset-2" data-testid="emergency-call-beyondblue">Beyond Blue 1300 22 4636</a>
  </p>
);

export default function Emergency() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutate: updateSession } = useUpdateSession();
  const { data: prefs, isFetched } = usePrefs();
  const { data: cards } = useCards();
  const [breathing, setBreathing] = useState(false);
  const [settled, setSettled] = useState(false);
  const fallback = useRef(null);
  const startedAt = useRef(0);
  const recorded = useRef(false);

  useEffect(() => {
    if (!breathing) return;
    const t = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(t);
  }, [breathing]);

  const recordTime = useCallback(() => {
    const secs = Math.round((Date.now() - startedAt.current) / 1000);
    if (recorded.current || !startedAt.current || secs < 2) return;
    recorded.current = true;
    updateSession({ id, patch: { emergencySeconds: secs } });
  }, [id, updateSession]);

  const begin = useCallback(() => {
    clearTimeout(fallback.current);
    setBreathing(true);
  }, []);

  const startGuide = useCallback(() => {
    setBreathing(false);
    playIntro(begin);
    clearTimeout(fallback.current);
    fallback.current = setTimeout(begin, 30000);
  }, [begin]);

  useEffect(() => {
    if (!isFetched) return;
    startedAt.current = Date.now();
    setAudioSelection({ ...DEFAULT_AUDIO, ...(prefs?.audio || {}) });
    startCalmTone();
    startGuide();
    return () => {
      clearTimeout(fallback.current);
      stopEmergencyAudio();
      recordTime();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  const onStable = () => {
    recordTime();
    stopEmergencyAudio();
    navigate(`/reflection/${id}`, { replace: true });
  };

  return (
    <div className="relative min-h-screen bg-[#0F1722] overflow-hidden" data-testid="emergency-screen">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[rgba(15,23,34,0.6)]" />
      <div className="relative min-h-screen flex flex-col items-center px-6 py-6 max-w-3xl mx-auto" style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))", paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
        <div className="w-full flex items-center">
          <div className="w-11" />
          <div className="flex-1 flex items-center justify-center gap-2.5">
            <Logo size={24} />
            <span className="text-white font-bold text-[19px]">Triggerific-Pilot</span>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button className="w-11 h-11 rounded-full bg-white/15 text-white grid place-items-center hover:bg-white/25 transition-colors" data-testid="override-audio-settings" aria-label="Sound settings">
                <SlidersHorizontal size={19} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 rounded-3xl bg-[#18222F]/95 backdrop-blur-xl border-white/15 p-5 text-white">
              <p className="font-bold mb-1">Calming sound</p>
              <p className="text-[13px] text-white/70 mb-4">Tones chosen to help lower cortisol and settle your nervous system.</p>
              <SoundPicker dark />
            </PopoverContent>
          </Popover>
        </div>

        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <p className="text-white font-semibold text-lg sm:text-xl max-w-md" data-testid="emergency-kicker">
            {breathing ? "Follow the circle. You're safe." : "You're safe. Listen, and follow when you're ready."}
          </p>
          <button onClick={startGuide} data-testid="replay-voice-button" className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 px-4 h-9 text-white text-[13px] font-medium transition-colors">
            <RotateCcw size={16} /> Replay voice
          </button>
        </div>

        <div className="flex-1 grid place-items-center py-6">
          <BreathingOrb size={300} running={breathing} onPhase={playCue} light />
        </div>

        {settled && cards?.length ? (
          <OverrideCards cards={cards} />
        ) : (
          <div className="flex flex-col gap-3 mb-6 w-full">
            {TIP_HINTS.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center justify-center gap-3 text-white/80">
                <Icon size={19} /> <span className="text-[15px]">{text}</span>
              </div>
            ))}
          </div>
        )}
        <Btn size="xl" className="w-full max-w-md !bg-white !text-[#0F1722]" label="I'm stable now" onClick={onStable} testId="stable-button" />
        {isFetched ? <CrisisStrip line={prefs?.crisisLine?.number ? prefs.crisisLine : defaultCrisisLine()} /> : <div className="mt-4 h-5" />}
      </div>
    </div>
  );
}
